import api, {
    AI_GENERATION,
    CUSTOM_DOMAINS_TYPE,
    EMAIL_LOG_TYPE,
    LIST,
    ORGANIZATION_ENTITLEMENTS_TYPE,
    PAYMENT_PROCESSOR_SETUP_DETAILS,
    PROMOTIONS_TYPE,
    SMTP_SETTINGS_TYPE,
    STOREFRONT_BANNERS,
    STOREFRONT_CARTS,
    STOREFRONT_CUSTOM_THEMES,
    STOREFRONT_LINKS,
    STOREFRONT_ORDERS,
    STOREFRONT_PAGES,
    STOREFRONT_SECTIONS,
    STOREFRONT_SETTINGS,
    STOREFRONT_STYLES,
    STOREFRONT_USERS
} from '../Api'
import {IActionResponse, IUploadFileResponse} from "../../dal/BaseDAL";
import {IBulkProductPromoRequest, IBulkProductPromoResponse, IPromotion, IPromotionProductsRequest, IPromotionProductsResponse} from "../../models/ProductPromo";
import {cloneDeep} from "lodash";
import {
    ICopyStorefrontRequest, ICopyStorefrontResponse,
    IEmailActivity,
    IGlobalStorefrontSettings,
    IEmailTemplateDefaults,
    IOpportunityTokenDefinition,
    TokenContext,
    IPaymentProcessorSetupDetails,
    ISmtpSettings,
    ISmtpSetupRequest,
    ISmtpTestRequest,
    ISmtpTestResponse,
    IStorefrontAdminSessionResponse,
    IStorefrontUserAccessResponse,
    IStorefrontUsersResponse,
    IStorefrontBundleEstimate,
    IStorefrontCart,
    IStorefrontCustomTheme,
    IStorefrontMonthlyOrders,
    IStorefrontOrder,
    IStorefrontOrderActivity,
    IStorefrontOrderStatuses,
    IStorefrontAvailableAttributesResponse,
    IStorefrontProductSuggestions,
    IStorefrontSettings,
    IStorefrontTheme,
    IStripeConfigRequest,
    IStripeSetupRequest,
    IStripeValidationResponse
} from "../../models/Storefront";
import { IStorefrontBundleView, IStorefrontProductView } from "../../models/StorefrontProducts";
import {ICountResponse} from "../../models/Search";
import {IAddCustomDomainRequest, ICustomDomain, IPatchCustomDomainRequest} from "../../models/CustomDomains";
import {ICollectionResponse} from "../../models/ICollectionResponse";
import {CRM_SLOT_DISABLED, ICrmFolioStorefrontSlotsRequest, ILookupConditions} from "../../models/Extension";
import {IStorefrontAiGeneration} from "../../models/Storefront";
import {EXTENSION_FOLIO_TYPE} from "../Api";

// Publish and revert do not touch settings alone: both copy a WHOLE set of staged artifacts between the
// draft and the live storefront — settings, sections, banners, pages, link menus and named styles. Anything
// still cached from before the copy is wrong afterwards, so every one of them is invalidated.
//
// Invalidating only the settings tag is what left the pages editor showing pre-revert sections until the
// browser was refreshed: the server had already put them back and nothing asked for them again.
const stagedArtifactTags = (id: string) => [
    { type: STOREFRONT_SETTINGS, id },
    { type: STOREFRONT_SECTIONS, id },
    { type: STOREFRONT_SECTIONS, id: LIST },
    { type: STOREFRONT_BANNERS, id },
    { type: STOREFRONT_PAGES, id },
    { type: STOREFRONT_PAGES, id: LIST },
    { type: STOREFRONT_LINKS, id },
    { type: STOREFRONT_STYLES, id },
    { type: STOREFRONT_STYLES, id: LIST },
] as const;

const storefrontSettingsEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        getGlobalStorefrontSettings: build.query<IGlobalStorefrontSettings, void>({
            query: () => 'storefront/settings/global'
        }),
        // The CRM wizard's "wire the storefront slots" step. Lives here rather than with the folio
        // endpoints because getGlobalStorefrontSettings has no tags: its writers keep the cache
        // truthful imperatively, and this mutation must do the same or the integrations pages would
        // show stale slot values after the wizard ran.
        setCrmFolioStorefrontSlots: build.mutation<IActionResponse, {folioId: string, request: ICrmFolioStorefrontSlotsRequest}>({
            query: (arg) => ({
                url: `extensions/folios/crm/${arg.folioId}/storefrontSlots`,
                method: 'PUT',
                body: arg.request
            }),
            invalidatesTags: (_res, _err, arg) => ([
                {type: EXTENSION_FOLIO_TYPE, id: arg.folioId},
                {type: EXTENSION_FOLIO_TYPE, id: LIST},
            ]),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    return;
                }
                // Mirror the server's composition (see CrmFolioSetupHandler.TryComposeSlotValue):
                // an identifier becomes the folio_{id}_{identifier} composite; DISABLED and the
                // empty string pass through; undefined leaves the slot alone.
                const compose = (requested?: string): string | undefined =>
                    requested === undefined || requested === '' || requested === CRM_SLOT_DISABLED
                        ? requested
                        : `folio_${arg.folioId}_${requested}`;
                dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getGlobalStorefrontSettings', undefined, (draft) => {
                        const opportunity = compose(arg.request.opportunity);
                        const customerLookup = compose(arg.request.customerLookup);
                        const locationLookup = compose(arg.request.locationLookup);
                        if (opportunity !== undefined) draft.opportunityExtension = opportunity;
                        if (customerLookup !== undefined) draft.customerLookupExtension = customerLookup;
                        if (locationLookup !== undefined) draft.locationLookupExtension = locationLookup;
                    })
                );
            }
        }),
        // Contexts mirror the API's OpportunityTokenCatalog.ForContext. Named "opportunity" for
        // historical reasons — opportunity overrides were the first consumer; webhook request bodies
        // read the same catalog through this endpoint.
        getEmailTemplateDefaults: build.query<IEmailTemplateDefaults, string>({
            query: (key) => `storefront/settings/emailDefaults/${key}`
        }),
        getOpportunityTokens: build.query<ICollectionResponse<IOpportunityTokenDefinition>, TokenContext>({
            query: (context) => `storefront/settings/opportunityTokens/${context}`
        }),
        // Self-serve custom domains (AC-1072). Org-level — verified domains land in the org's
        // storefrontDomains entitlement, so mutations also invalidate the entitlements tag.
        getCustomDomains: build.query<ICollectionResponse<ICustomDomain>, void>({
            query: () => 'storefront/settings/domains',
            providesTags: [CUSTOM_DOMAINS_TYPE]
        }),
        addCustomDomain: build.mutation<IActionResponse, IAddCustomDomainRequest>({
            query: (body) => ({
                url: 'storefront/settings/domains',
                method: 'POST',
                body
            }),
            invalidatesTags: [CUSTOM_DOMAINS_TYPE]
        }),
        patchCustomDomain: build.mutation<IActionResponse, IPatchCustomDomainRequest>({
            query: ({id, ...body}) => ({
                url: `storefront/settings/domains/${id}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: [CUSTOM_DOMAINS_TYPE]
        }),
        refreshCustomDomain: build.mutation<ICustomDomain, string>({
            query: (domainId) => ({
                url: `storefront/settings/domains/${domainId}/refresh`,
                method: 'POST'
            }),
            // Entitlements only change on the pending→active transition (the verified hostname is
            // appended to storefrontDomains); the routine poll shouldn't refetch them every tick.
            invalidatesTags: (result) => result?.status === 'active'
                ? [CUSTOM_DOMAINS_TYPE, ORGANIZATION_ENTITLEMENTS_TYPE]
                : [CUSTOM_DOMAINS_TYPE]
        }),
        deleteCustomDomain: build.mutation<IActionResponse, string>({
            query: (domainId) => ({
                url: `storefront/settings/domains/${domainId}`,
                method: 'DELETE'
            }),
            invalidatesTags: [CUSTOM_DOMAINS_TYPE, ORGANIZATION_ENTITLEMENTS_TYPE]
        }),
        saveGlobalStorefrontSettings: build.mutation<IActionResponse, IGlobalStorefrontSettings>({
            query: (arg) => ({
                url: 'storefront/settings/global',
                method: 'PUT',
                body: arg
            }),
            async onQueryStarted(arg, {dispatch}) {
                dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getGlobalStorefrontSettings', undefined, (draft) => {
                        Object.assign(draft, {...arg})
                    })
                );
            }
        }),
        patchGlobalStorefrontSettings: build.mutation<IActionResponse, Partial<IGlobalStorefrontSettings>>({
            query: (arg) => ({
                url: 'storefront/settings/global',
                method: 'PATCH',
                body: arg
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getGlobalStorefrontSettings', undefined, (draft) => {
                        Object.assign(draft, arg);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),
        runDynamicProductListings: build.mutation<IActionResponse, string | undefined>({
            query: () => ({
                url: (`storefront/processDynamicProductListings`),
                method: 'POST'
            }),
        }),
        // Queues an immediate recommendations run for the whole organization. Normally the Scheduler
        // does this during low-usage hours; this exists so a demo or a verification pass doesn't have
        // to wait for the next window. The API re-checks the org entitlement.
        runProductRecommendations: build.mutation<IActionResponse, void>({
            query: () => ({
                url: (`storefront/processProductRecommendations`),
                method: 'POST'
            }),
        }),
        // Queues an immediate search-suggestion rebuild. Pass a storefront id to rebuild just that
        // storefront (what the per-storefront settings page does); omit it to cover the whole org, the
        // way the Scheduler does. The API re-checks the org entitlement either way.
        runSearchSuggestions: build.mutation<IActionResponse, string | void>({
            query: (storefront) => ({
                url: storefront
                    ? `storefront/processSearchSuggestions?storefront=${encodeURIComponent(storefront)}`
                    : `storefront/processSearchSuggestions`,
                method: 'POST'
            }),
        }),
        getStorefrontProductCount: build.query<ICountResponse, void>({
            query: () => 'storefront/products/count'
        }),
        getStorefrontLoginLink: build.query<IStorefrontAdminSessionResponse, string>({
            query: (arg) => `keyAuth/storefront/${arg}/adminLogin`
        }),
        getStorefronts: build.query<ICollectionResponse<IStorefrontSettings>, void>({
            query: () => 'storefront/storefronts',
            providesTags: () => [{ type: STOREFRONT_SETTINGS, id: LIST }]
        }),
        getStorefront: build.query<IStorefrontSettings, string>({
            query: (arg) => `storefront/storefronts/${arg}`,
            providesTags: (_result, _error, id) => [{ type: STOREFRONT_SETTINGS, id }]
        }),
        // Which storefronts the AI design assistant built, and which one it is working on right now.
        // Rows only exist for stores that have had an assistant session, so most orgs get an empty list.
        // Tagged on its own id rather than LIST: an ordinary storefront save has no bearing on it, and
        // sharing the tag would refetch this on every storefront edit in the app.
        getStorefrontAiGenerations: build.query<ICollectionResponse<IStorefrontAiGeneration>, void>({
            query: () => 'storefront/storefronts/aiGeneration',
            providesTags: () => [{ type: STOREFRONT_SETTINGS, id: AI_GENERATION }]
        }),
        // Who can sign in to a storefront. Server-resolved: a person's access can come from their own
        // contact record, their company, the org default, or an entry added on this screen — so this is
        // a view the API computes, not a stored list, and paging/sorting/searching happen there too.
        getStorefrontUsers: build.query<IStorefrontUsersResponse, { storefront: string, page?: number, pageSize?: number, sortBy?: string, sortDirection?: string, searchTerm?: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/users`,
                params: {
                    page: arg.page, pageSize: arg.pageSize, sortBy: arg.sortBy,
                    sortDirection: arg.sortDirection, searchTerm: arg.searchTerm,
                },
            }),
            providesTags: (_r, _e, arg) => [{ type: STOREFRONT_USERS, id: arg.storefront }],
        }),
        addStorefrontUsers: build.mutation<IStorefrontUserAccessResponse, { storefront: string, contactIds: string[] }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/users`,
                method: 'POST',
                body: { contactIds: arg.contactIds },
            }),
            invalidatesTags: (_r, _e, arg) => [{ type: STOREFRONT_USERS, id: arg.storefront }],
        }),
        // Settles a pending signup. Approving also emails the person that they can now sign in.
        setStorefrontUserApproval: build.mutation<IStorefrontUserAccessResponse, { storefront: string, contactIds: string[], approve: boolean }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/users/${arg.approve ? 'approve' : 'deny'}`,
                method: 'POST',
                body: { contactIds: arg.contactIds },
            }),
            invalidatesTags: (_r, _e, arg) => [{ type: STOREFRONT_USERS, id: arg.storefront }],
        }),
        removeStorefrontUsers: build.mutation<IStorefrontUserAccessResponse, { storefront: string, contactIds: string[] }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/users/remove`,
                method: 'POST',
                body: { contactIds: arg.contactIds },
            }),
            invalidatesTags: (_r, _e, arg) => [{ type: STOREFRONT_USERS, id: arg.storefront }],
        }),
        // Distinct product facet attributes available across a storefront's products, plus the org's cap.
        // Feeds the (deferred) attribute picker.
        getStorefrontAvailableAttributes: build.query<IStorefrontAvailableAttributesResponse, string>({
            query: (storefront) => `storefront/storefronts/${storefront}/products/attributes`
        }),
        // Products and bundles worth putting on a storefront. Read-only and non-committal: the response is a
        // proposal, and the bulk listing call is what writes. `size` caps the set, `excludeListed` drops anything
        // already on this storefront, and `sources` narrows to particular reasons (for example purchaseHistory
        // only, with no in-stock catalog filler).
        //
        // Deliberately untagged. The first-run flow reads it once behind a stage the user is already editing, and
        // the products tab hides itself when the set is empty, so a tag would only cause refetch churn under both.
        // Callers that add from it refetch the grid themselves.
        getStorefrontProductSuggestions: build.query<IStorefrontProductSuggestions, {storefront: string, size?: number, excludeListed?: boolean, sources?: string, includeOutOfStock?: boolean, bundleSize?: number}>({
            query: (arg) => {
                const params = new URLSearchParams();
                if (arg.size !== undefined) params.append('size', String(arg.size));
                if (arg.excludeListed !== undefined) params.append('excludeListed', String(arg.excludeListed));
                if (arg.sources) params.append('sources', arg.sources);
                if (arg.includeOutOfStock !== undefined) params.append('includeOutOfStock', String(arg.includeOutOfStock));
                // Bundles are counted separately from products: the bundle strip wants a handful and the
                // bundle panel a full page, neither of which is the product size. The API caps this at 12.
                if (arg.bundleSize !== undefined) params.append('bundleSize', String(arg.bundleSize));
                const qs = params.toString();
                return `storefront/storefronts/${arg.storefront}/productSuggestions${qs ? `?${qs}` : ''}`;
            }
        }),
        // Puts one bundle on one storefront, additively: the bundle's existing storefront list is unioned with
        // this one, so calling it twice is the same as calling it once.
        //
        // Deliberately untagged, like the suggestions read it is driven from. The bundles grid carries no tags
        // of its own, so callers refetch it themselves after a bundle lands.
        listBundleOnStorefront: build.mutation<IActionResponse, {storefront: string, bundleId: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/bundles/${arg.bundleId}/listing`,
                method: 'POST',
            }),
        }),
        resetStorefrontCacheSeed: build.mutation<IActionResponse, {subUrl: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/resetCacheSeed/${arg.subUrl}`,
                method: 'POST'
            }),
        }),
        saveStorefront: build.mutation<IActionResponse, {storefront:IStorefrontSettings, id: string}>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.id}`,
                method: 'PUT',
                body: arg.storefront
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {

                const singleUpdate = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefront', arg.id, (draft) => {
                        Object.assign(draft, {...arg.storefront})
                    })
                );
                const multiUpdate = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefronts', undefined, (draft) => {

                        const existingIndex = draft.items?.findIndex(a => a.id === arg.id)
                        if(existingIndex != -1){
                            draft.items[existingIndex] = arg.storefront
                        }
                    })
                );
                try {
                    const result = await queryFulfilled;
                    if (!result.data.success){
                        singleUpdate.undo();
                        multiUpdate.undo();
                    }
                }
                catch{
                    singleUpdate.undo();
                    multiUpdate.undo();
                }

            }
        }),
        patchStorefront: build.mutation<IActionResponse, { id: string; patch: Partial<IStorefrontSettings> }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.id}`,
                method: 'PATCH',
                body: arg.patch
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const singleUpdate = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefront', arg.id, (draft) => {
                        Object.assign(draft, arg.patch);
                    })
                );
                const multiUpdate = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefronts', undefined, (draft) => {
                        const existingIndex = draft.items?.findIndex(a => a.id === arg.id)
                        if (existingIndex != null && existingIndex !== -1) {
                            Object.assign(draft.items[existingIndex], arg.patch)
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    singleUpdate.undo();
                    multiUpdate.undo();
                }
            }
        }),
        publishStorefront: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `storefront/storefronts/${id}/publish`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, id) => stagedArtifactTags(id)
        }),
        revertStorefront: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `storefront/storefronts/${id}/revert`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, id) => stagedArtifactTags(id)
        }),
        createStorefront: build.mutation<IActionResponse, IStorefrontSettings>({
            query: (arg) => ({
                url: 'storefront/storefronts',
                method: 'POST',
                body: arg
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const result = await queryFulfilled;
                dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefronts', undefined, (draft) => {
                        const newSf = {...arg}
                        newSf.id = result.data.id

                        const storefronts = cloneDeep(draft);
                        storefronts.items.push(newSf)
                        Object.assign(draft, storefronts)
                    })
                );
            }
        }),
        softDeleteStorefront: build.mutation<IActionResponse, string>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg}/softDelete`,
                method: 'DELETE'
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {

                const result = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefronts', undefined, (draft) => {
                        
                        const storefronts = cloneDeep(draft);
                        const index = storefronts?.items?.findIndex((a) => a.id === arg)
                        
                        if(index && index != -1){
                            storefronts.items.splice(index, 1)
                            Object.assign(draft, storefronts)
                        }
                    })
                );
                
                try {
                    await queryFulfilled
                }
                catch{
                    result.undo()
                }
            }
        }),
        copyStorefront: build.mutation<ICopyStorefrontResponse, ICopyStorefrontRequest>({
            query: (arg) => ({
                url: 'storefront/storefronts/copy',
                method: 'POST',
                body: arg
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const result = await queryFulfilled;
                dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefronts', undefined, (draft) => {
                        if(result.data.storefront) {
                            const storefronts = cloneDeep(draft);
                            storefronts.items.push(result.data.storefront)
                            Object.assign(draft, storefronts)
                        }
                    })
                );
            }
        }),
        getStorefrontOrders: build.query<ICollectionResponse<IStorefrontOrder>, { conditions?: ILookupConditions } >({
            query: (arg) => ({
                url: 'storefront/orders/search',
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            // query: (arg) => `storefront/orders` + getQueryStringFromConditions(arg.conditions),
            serializeQueryArgs: ({endpointName}) => {
                return endpointName
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: (result) => result?.items ?
                [
                    ...result.items.map(c => ({type: STOREFRONT_ORDERS, id: c.id} as const)),
                    {type: STOREFRONT_ORDERS, id: LIST}
                ]
                : [{type: STOREFRONT_ORDERS, id: LIST}]
        }),
        // Small keyword search for the global search palette. getStorefrontOrders collapses every
        // arg onto one cache entry for the orders grid, so a second subscriber with different args
        // would clobber it — this one keeps RTK's default per-argument caching instead.
        searchStorefrontOrderRefs: build.query<ICollectionResponse<IStorefrontOrder>, { searchTerm: string, pageSize?: number }>({
            query: (arg) => ({
                url: 'storefront/orders/search',
                method: 'POST',
                // The server only applies searchTerm alongside a searchBy field (a bare term is
                // silently ignored), and the wildcard search path only works on string fields, so
                // order numbers are not searchable here — matches are on the customer email.
                body: { page: 1, pageSize: arg.pageSize ?? 5, searchTerm: arg.searchTerm, searchBy: 'emailAddress' }
            }),
        }),
        getStorefrontOrder: build.query<IStorefrontOrder, string >({
            query: (arg) => `storefront/orders/${arg}`
        }),
        getStorefrontOrderActivities: build.query<ICollectionResponse<IStorefrontOrderActivity>, { id: string, count?: number }>({
            query: (arg) => `storefront/orders/${arg.id}/activities` + (arg.count ? `?count=${arg.count}` : ''),
            providesTags: (_result, _error, arg) => [{type: STOREFRONT_ORDERS, id: arg.id}]
        }),
        processStorefrontOrder: build.mutation<IActionResponse, {id: string, steps?: string[]}>({
            query: (arg) => ({
                url: `storefront/orders/${arg.id}/process`,
                method: 'POST',
                body: { steps: arg.steps }
            }),
            invalidatesTags: () => [{type: STOREFRONT_ORDERS, id: LIST}]
        }),
        getStorefrontOrderStatuses: build.query<IStorefrontOrderStatuses, void>({
            query: () => 'storefront/orders/statuses'
        }),
        addStorefrontOrderShipment: build.mutation<IActionResponse, {id: string, storefront: string; carrier?: string, trackingNumber?: string, trackingUrl?: string}>({
            query: (arg) => ({
                url: `storefront/orders/${arg.id}/shipments`,
                method: 'POST',
                body: { carrier: arg.carrier, trackingNumber: arg.trackingNumber, trackingUrl: arg.trackingUrl }
            }),
            invalidatesTags: (_result, _error, arg) => [{type: STOREFRONT_ORDERS, id: arg.id}],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;
                    const newShipment = {
                        shipmentTrackingUrl: arg.trackingUrl,
                        shipmentCarrier: arg.carrier,
                        shipmentTrackingNumber: arg.trackingNumber,
                        shipmentTrackingId: result.data.id
                    };
                    dispatch(
                        storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrder', arg.id, draft => {
                            draft.shipments = [...(draft.shipments ?? []), newShipment];
                        })
                    );
                    dispatch(
                        storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrders', {}, draft => {
                            const i = draft.items.findIndex(i => i.id === arg.id);
                            if(i > -1) {
                                const shipments = draft.items[i].shipments ?? []
                                shipments.push({shipmentTrackingUrl: arg.trackingUrl, shipmentCarrier: arg.carrier, shipmentTrackingNumber: arg.trackingNumber, shipmentTrackingId: result.data.id})
                                draft.items[i].shipments = shipments;
                            }
                        })
                    );
                    dispatch(
                        storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefront', arg.storefront, draft => {
                            const i = draft.items.findIndex(i => i.id === arg.id);
                            if(i > -1) {
                                const shipments = draft.items[i].shipments ?? []
                                shipments.push({shipmentTrackingUrl: arg.trackingUrl, shipmentCarrier: arg.carrier, shipmentTrackingNumber: arg.trackingNumber, shipmentTrackingId: result.data.id})
                                draft.items[i].shipments = shipments;
                            }
                        })
                    );
                    dispatch(
                        storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefrontSearch', { storefrontId: arg.storefront }, draft => {
                            const i = draft.items.findIndex(i => i.id === arg.id);
                            if(i > -1) {
                                draft.items[i].shipments = [...(draft.items[i].shipments ?? []), newShipment];
                            }
                        })
                    );
                } catch {
                    // invalidatesTags will trigger a list refetch on failure
                }
            }
        }),
        updateStorefrontOrderShipment: build.mutation<IActionResponse, {id: string, storefront: string, shipmentTrackingId: string, carrier?: string, trackingNumber?: string, trackingUrl?: string}>({
            query: (arg) => ({
                url: `storefront/orders/${arg.id}/shipments/${arg.shipmentTrackingId}`,
                method: 'PUT',
                body: { carrier: arg.carrier, trackingNumber: arg.trackingNumber, trackingUrl: arg.trackingUrl }
            }),
            invalidatesTags: (_result, _error, arg) => [{type: STOREFRONT_ORDERS, id: arg.id}],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const applyToShipment = (shipments: {shipmentTrackingId?: string, shipmentCarrier?: string, shipmentTrackingNumber?: string, shipmentTrackingUrl?: string}[] | undefined) => {
                    const idx = shipments?.findIndex(s => s.shipmentTrackingId === arg.shipmentTrackingId) ?? -1;
                    const shipment = idx >= 0 ? shipments?.[idx] : undefined;
                    if (!shipment) return;
                    shipment.shipmentCarrier = arg.carrier;
                    shipment.shipmentTrackingNumber = arg.trackingNumber;
                    shipment.shipmentTrackingUrl = arg.trackingUrl;
                };
                const listPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrders', {}, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) applyToShipment(draft.items[i].shipments);
                    })
                );
                const singlePatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrder', arg.id, draft => {
                        applyToShipment(draft.shipments);
                    })
                );
                const storefrontListPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefront', arg.storefront, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) applyToShipment(draft.items[i].shipments);
                    })
                );
                const storefrontSearchListPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefrontSearch', { storefrontId: arg.storefront }, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) applyToShipment(draft.items[i].shipments);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    listPatch.undo();
                    singlePatch.undo();
                    storefrontListPatch.undo();
                    storefrontSearchListPatch.undo();
                }
            }
        }),
        deleteStorefrontOrderShipment: build.mutation<IActionResponse, {id: string, storefront: string, shipmentTrackingId: string}>({
            query: (arg) => ({
                url: `storefront/orders/${arg.id}/shipments/${arg.shipmentTrackingId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, arg) => [{type: STOREFRONT_ORDERS, id: arg.id}],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const removeShipment = (shipments: {shipmentTrackingId?: string}[] | undefined) => {
                    const idx = shipments?.findIndex(s => s.shipmentTrackingId === arg.shipmentTrackingId) ?? -1;
                    if (idx >= 0 && shipments) shipments.splice(idx, 1);
                };
                const listPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrders', {}, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) removeShipment(draft.items[i].shipments);
                    })
                );
                const singlePatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrder', arg.id, draft => {
                        removeShipment(draft.shipments);
                    })
                );
                const storefrontListPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefront', arg.storefront, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) removeShipment(draft.items[i].shipments);
                    })
                );
                const storefrontSearchListPatch = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrdersForStorefrontSearch', { storefrontId: arg.storefront }, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        if (i > -1) removeShipment(draft.items[i].shipments);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    listPatch.undo();
                    singlePatch.undo();
                    storefrontListPatch.undo();
                    storefrontSearchListPatch.undo();
                }
            }
        }),
        updateStorefrontOrderStatus: build.mutation<IActionResponse, {id: string, status: string}>({
            query: (arg) => ({
                url: `storefront/orders/${arg.id}/status/${arg.status}`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [{type: STOREFRONT_ORDERS, id: LIST}, {type: STOREFRONT_ORDERS, id: arg.id}],
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    storefrontSettingsEndpoints.util.updateQueryData('getStorefrontOrders', {}, draft => {
                        const i = draft.items.findIndex(i => i.id === arg.id);
                        draft.items[i].status = arg.status;
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),
        getStorefrontMonthlyOrders: build.query<ICollectionResponse<IStorefrontMonthlyOrders>, {year:number}>({
            query: (arg) => `storefront/orders/monthly/${arg.year}`
        }),
        getStorefrontOrdersForStorefront: build.query<ICollectionResponse<IStorefrontOrder>, string>({
            query: (arg) => `storefront/orders/${arg}`
        }),
        getStorefrontThemes: build.query<{[key:string]: IStorefrontTheme}, void>({
            query: () => 'storefront/settings/themes'
        }),
        getPaymentProcessorSetup: build.query<IPaymentProcessorSetupDetails, void>({
            query: () => 'storefront/settings/paymentProcessor',
            providesTags: () => [{type: PAYMENT_PROCESSOR_SETUP_DETAILS, id: LIST}],
        }),
        setupStripe: build.mutation<IActionResponse, IStripeSetupRequest>({
            query: (arg) => ({
                url: 'storefront/settings/paymentProcessor/stripe',
                method: 'POST',
                body: arg
            }),
            invalidatesTags: () => [{type: PAYMENT_PROCESSOR_SETUP_DETAILS, id: LIST}],
        }),
        saveStripeConfig: build.mutation<IActionResponse, IStripeConfigRequest>({
            query: (arg) => ({
                url: 'storefront/settings/paymentProcessor/stripe/config',
                method: 'PUT',
                body: arg
            }),
            invalidatesTags: () => [{type: PAYMENT_PROCESSOR_SETUP_DETAILS, id: LIST}],
        }),
        validateStripe: build.mutation<IStripeValidationResponse, { returnUrl?: string }>({
            query: (arg) => ({
                url: 'storefront/settings/paymentProcessor/stripe/validate',
                method: 'POST',
                body: arg
            }),
        }),
        getSmtpSettings: build.query<ISmtpSettings, void>({
            query: () => 'storefront/settings/smtp',
            providesTags: () => [{type: SMTP_SETTINGS_TYPE, id: LIST}],
        }),
        setupSmtp: build.mutation<IActionResponse, ISmtpSetupRequest>({
            query: (arg) => ({
                url: 'storefront/settings/smtp',
                method: 'POST',
                body: arg
            }),
            invalidatesTags: () => [{type: SMTP_SETTINGS_TYPE, id: LIST}],
        }),
        patchSmtp: build.mutation<IActionResponse, ISmtpSetupRequest>({
            query: (arg) => ({
                url: 'storefront/settings/smtp',
                method: 'PATCH',
                body: arg
            }),
            invalidatesTags: () => [{type: SMTP_SETTINGS_TYPE, id: LIST}],
        }),
        testSmtp: build.mutation<ISmtpTestResponse, ISmtpTestRequest>({
            query: (arg) => ({
                url: 'storefront/settings/smtp/test',
                method: 'POST',
                body: arg
            }),
        }),
        getEmailLog: build.query<IEmailActivity[], { count?: number } | void>({
            query: (arg) => `storefront/settings/smtp/log` + (arg?.count ? `?count=${arg.count}` : ''),
            providesTags: () => [{type: EMAIL_LOG_TYPE, id: LIST}],
        }),
        getStorefrontCustomThemes: build.query<ICollectionResponse<IStorefrontCustomTheme>, void>({
            query: () => 'storefront/settings/themes/custom',
            providesTags: () => [{type: STOREFRONT_CUSTOM_THEMES, id: LIST}]
        }),
        getStorefrontCustomTheme: build.query<IStorefrontCustomTheme, string>({
            query: (arg) => `storefront/settings/themes/custom/${arg}`,
        }),
        saveStorefrontCustomTheme: build.mutation<IActionResponse, {theme: IStorefrontCustomTheme}>({
            query: (arg) => ({
                url: arg.theme.id ? `storefront/settings/themes/custom/${arg.theme.id}` : 'storefront/settings/themes/custom',
                method: arg.theme.id ? 'PUT' : 'POST',
                body: arg.theme
            }),
            invalidatesTags: () => [{type: STOREFRONT_CUSTOM_THEMES, id: LIST}],
        }),
        deleteStorefrontCustomTheme: build.mutation<IActionResponse, string>({
            query: (arg) => ({
                url: `storefront/settings/themes/custom/${arg}`,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{type: STOREFRONT_CUSTOM_THEMES, id: LIST}],
        }),
        uploadStorefrontFile: build.mutation<IUploadFileResponse, { file: string, fileName: string, type: string }>({
            query: (arg) => ({
                url: `storefront/files/upload`,
                method: 'POST',
                body: arg
            })
        }),
        getStorefrontBundleEstimate: build.mutation<IStorefrontBundleEstimate, {storefrontId: string, bundleId: string, minimumPrice: number, markupType: string, markup: number}>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/bundles/${arg.bundleId}/estimate`,
                method: 'POST',
                body: {
                    markupType: arg.markupType,
                    markup: arg.markup,
                    minimumPrice: arg.minimumPrice
                }

            }),
        }),
        getStorefrontCarts: build.query<ICollectionResponse<IStorefrontCart>, { conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: 'storefront/carts/search',
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({endpointName}) => {
                return endpointName
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: (result) => result?.items ?
                [
                    ...result.items.map(c => ({type: STOREFRONT_CARTS, id: c.id} as const)),
                    {type: STOREFRONT_CARTS, id: LIST}
                ]
                : [{type: STOREFRONT_CARTS, id: LIST}]
        }),
        // Per-argument-cached counterpart of getStorefrontCarts for the global search palette;
        // same rationale as searchStorefrontOrderRefs above.
        searchStorefrontCartRefs: build.query<ICollectionResponse<IStorefrontCart>, { searchTerm: string, pageSize?: number }>({
            query: (arg) => ({
                url: 'storefront/carts/search',
                method: 'POST',
                // searchBy required — see searchStorefrontOrderRefs. Carts are found by email.
                body: { page: 1, pageSize: arg.pageSize ?? 5, searchTerm: arg.searchTerm, searchBy: 'emailAddress' }
            }),
        }),
        getStorefrontCart: build.query<IStorefrontCart, string>({
            query: (arg) => `storefront/carts/${arg}`,
            providesTags: (_result, _error, id) => [{type: STOREFRONT_CARTS, id}]
        }),
        getStorefrontOrdersForStorefrontSearch: build.query<ICollectionResponse<IStorefrontOrder>, { storefrontId: string, conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/orders/search`,
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getStorefrontCartsForStorefrontSearch: build.query<ICollectionResponse<IStorefrontCart>, { storefrontId: string, conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/carts/search`,
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        searchAdminStorefrontProducts: build.query<ICollectionResponse<IStorefrontProductView>, { storefrontId: string, conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/admin/products/search`,
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        // Applies one MSP promo across the selected products. Vendor promos are not created here —
        // those are a promotional cost and arrive with the vendor's own data through the extensions.
        bulkApplyProductPromo: build.mutation<IBulkProductPromoResponse, { catalog: string, request: IBulkProductPromoRequest }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/products/promos/bulk`,
                method: 'POST',
                body: arg.request
            }),
        }),
        // Promotion campaigns. The values live here, not on the products, so an edit through this
        // endpoint moves every product on the campaign with nothing to reindex.
        getPromotions: build.query<IPromotion[], { catalog: string, storefront?: string, includeInactive?: boolean }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/promotions`,
                method: 'GET',
                params: {
                    storefront: arg.storefront,
                    includeInactive: arg.includeInactive ?? true,
                },
            }),
            providesTags: [PROMOTIONS_TYPE],
        }),
        getPromotion: build.query<IPromotion, { catalog: string, id: string }>({
            query: (arg) => ({ url: `catalog/${arg.catalog}/promotions/${arg.id}`, method: 'GET' }),
            providesTags: [PROMOTIONS_TYPE],
        }),
        createPromotion: build.mutation<IPromotion, { catalog: string, request: IPromotion }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/promotions`,
                method: 'POST',
                body: arg.request,
            }),
            invalidatesTags: [PROMOTIONS_TYPE],
        }),
        // Adds products to an existing campaign. Writes only the link — the markup, window and scope
        // stay on the promotion, which is what keeps one edit moving every product on it.
        addProductsToPromotion: build.mutation<IPromotionProductsResponse, { catalog: string, id: string, request: IPromotionProductsRequest }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/promotions/${arg.id}/products`,
                method: 'POST',
                body: arg.request,
            }),
            invalidatesTags: [PROMOTIONS_TYPE],
        }),
        removeProductsFromPromotion: build.mutation<IPromotionProductsResponse, { catalog: string, id: string, request: IPromotionProductsRequest }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/promotions/${arg.id}/products/remove`,
                method: 'PUT',
                body: arg.request,
            }),
            invalidatesTags: [PROMOTIONS_TYPE],
        }),
        updatePromotion: build.mutation<IPromotion, { catalog: string, id: string, request: IPromotion }>({
            query: (arg) => ({
                url: `catalog/${arg.catalog}/promotions/${arg.id}`,
                method: 'PUT',
                body: arg.request,
            }),
            invalidatesTags: [PROMOTIONS_TYPE],
        }),
        deletePromotion: build.mutation<IActionResponse, { catalog: string, id: string }>({
            query: (arg) => ({ url: `catalog/${arg.catalog}/promotions/${arg.id}`, method: 'DELETE' }),
            invalidatesTags: [PROMOTIONS_TYPE],
        }),
        searchAdminStorefrontSavedSearchProducts: build.query<ICollectionResponse<IStorefrontProductView>, { storefrontId: string, conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/admin/saved-search-products/search`,
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        // `unlisted` widens the search from the bundles this storefront carries to every bundle the
        // organization has, still priced for this storefront. It is part of the cache key as well as the
        // query string: the two searches answer different questions and must not share a cache entry.
        searchAdminStorefrontBundles: build.query<ICollectionResponse<IStorefrontBundleView>, { storefrontId: string, conditions?: ILookupConditions, unlisted?: boolean }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefrontId}/admin/bundles/search${arg.unlisted ? '?unlisted=true' : ''}`,
                method: 'POST',
                body: arg.conditions ?? {}
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}-${queryArgs.unlisted === true}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
    }),
    overrideExisting: false,
})

export const {
    useCreatePromotionMutation,
    useAddProductsToPromotionMutation,
    useRemoveProductsFromPromotionMutation,
    useGetPromotionsQuery,
    useGetPromotionQuery,
    useUpdatePromotionMutation,
    useDeletePromotionMutation,
    useGetStorefrontProductCountQuery,
    useGetStorefrontThemesQuery,
    useGetStorefrontCustomThemesQuery,
    useGetStorefrontCustomThemeQuery,
    useSaveStorefrontCustomThemeMutation,
    useDeleteStorefrontCustomThemeMutation,
    useUploadStorefrontFileMutation,
    useGetPaymentProcessorSetupQuery,
    useSetupStripeMutation,
    useSaveStripeConfigMutation,
    useValidateStripeMutation,
    useGetSmtpSettingsQuery,
    useSetupSmtpMutation,
    usePatchSmtpMutation,
    useTestSmtpMutation,
    useGetEmailLogQuery,
    useGetGlobalStorefrontSettingsQuery,
    useLazyGetGlobalStorefrontSettingsQuery,
    useGetCustomDomainsQuery,
    useAddCustomDomainMutation,
    usePatchCustomDomainMutation,
    useRefreshCustomDomainMutation,
    useDeleteCustomDomainMutation,
    useGetEmailTemplateDefaultsQuery,
   useGetOpportunityTokensQuery,
    useSaveGlobalStorefrontSettingsMutation,
    usePatchGlobalStorefrontSettingsMutation,
    useSetCrmFolioStorefrontSlotsMutation,
    useGetStorefrontsQuery,
    useGetStorefrontAiGenerationsQuery,
    useGetStorefrontQuery,
    useGetStorefrontAvailableAttributesQuery,
    useGetStorefrontProductSuggestionsQuery,
    useListBundleOnStorefrontMutation,
    useSaveStorefrontMutation,
    usePatchStorefrontMutation,
    usePublishStorefrontMutation,
    useRevertStorefrontMutation,
    useCreateStorefrontMutation,
    useGetStorefrontOrdersQuery,
    useSearchStorefrontOrderRefsQuery,
    useSearchStorefrontCartRefsQuery,
    useProcessStorefrontOrderMutation,
    useGetStorefrontMonthlyOrdersQuery,
    useGetStorefrontOrdersForStorefrontQuery,
    useGetStorefrontOrderStatusesQuery,
    useUpdateStorefrontOrderStatusMutation,
    useAddStorefrontOrderShipmentMutation,
    useUpdateStorefrontOrderShipmentMutation,
    useDeleteStorefrontOrderShipmentMutation,
    useLazyGetStorefrontLoginLinkQuery,
    useGetStorefrontOrderQuery,
    useGetStorefrontOrderActivitiesQuery,
    useRunDynamicProductListingsMutation,
    useRunProductRecommendationsMutation,
    useRunSearchSuggestionsMutation,
    useGetStorefrontBundleEstimateMutation,
    useLazyGetStorefrontsQuery,
    useCopyStorefrontMutation,
    useSoftDeleteStorefrontMutation,
    useResetStorefrontCacheSeedMutation,
    useGetStorefrontCartsQuery,
    useGetStorefrontCartQuery,
    useGetStorefrontOrdersForStorefrontSearchQuery,
    useGetStorefrontCartsForStorefrontSearchQuery,
    useSearchAdminStorefrontProductsQuery,
    useBulkApplyProductPromoMutation,
    useSearchAdminStorefrontSavedSearchProductsQuery,
    useSearchAdminStorefrontBundlesQuery,
    useLazySearchAdminStorefrontProductsQuery,
    useLazySearchAdminStorefrontSavedSearchProductsQuery,
    useLazySearchAdminStorefrontBundlesQuery,
    useGetStorefrontUsersQuery,
    useAddStorefrontUsersMutation,
    useRemoveStorefrontUsersMutation,
    useSetStorefrontUserApprovalMutation,
} = storefrontSettingsEndpoints
